import { useCallback, useMemo } from 'react'
import { Col, Button } from 'antd'
import { BorderOuterOutlined } from '@ant-design/icons'
import { isProdSite, renderTranslation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  marketplaceSectionId,
  movementSectionId,
  priceGenerationSectionId,
  quantityLimitSectionId,
  quantitySectionId,
  gridLayoutContainerIdRelation,
  imagesSectionId,
  descriptionsSectionId,
  pubblcationsSectionId,
  barcodesSectionId,
} from '../../../Constants/Configuration'
import { retrieveSelectedArticleKey } from '../../../Helpers/ArticlesHelper'
import { setGridColumn, setStoredGridLayout } from '../../../Slices/Kiosk/GridLayoutSlice'
import { changeGridColumns } from '../../../Helpers/GridLayoutHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'

import {
  handleArticleQuantities,
  handleImageRetrieve,
  handleMarketplaceAvailabilitiesData,
  handleMovementsData,
  handlePricesData,
  handleQuantityLimitData,
  handleDescriptions,
  handlePubblications,
  handleBarcodes,
} from 'Services/Kiosk/ArticleServices'
import UpperMenu from './UpperMenu'

function GridLayoutButtons(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const quantitiesTabVisible = useAppSelector(state => state.present.configuration.isTabQuantitiesVisibile)
  const workspaceQuantitiesTabVisible = useAppSelector(state => state.present.configuration.workspaceQuantitiesVisible)
  const movementTabVisible = useAppSelector(state => state.present.configuration.isTabMovementVisible)
  const workspaceMovementVisible = useAppSelector(state => state.present.configuration.workspaceMovementVisible)
  const availabilityTabVisible = useAppSelector(state => state.present.configuration.isTabAvailabilityVisibile)
  const workspaceAvailabilityVisible = useAppSelector(state => state.present.configuration.workspaceAvailabilityVisible)
  const isMarketplaceAvailabilitiesEnable = useAppSelector(
    state => state.present.configuration.isMarketplaceAvailabilitiesEnable,
  )
  const workspaceMarketplaceAvailabilitiesVisible = useAppSelector(
    state => state.present.configuration.workspaceMarketplaceAvailabilitiesVisible,
  )
  const workspacePricesVisible = useAppSelector(state => state.present.configuration.workspacePricesVisible)
  const workspaceQuantityLimitVisible = useAppSelector(
    state => state.present.configuration.workspaceQuantityLimitVisible,
  )
  const workspaceDiwaReactGridLayoutColumnsBtnVisibile = useAppSelector(
    state => state.present.configuration.workspaceDiwaReactGridLayoutColumnsBtnVisibile,
  )
  const workspaceImagesVisible = useAppSelector(state => state.present.configuration.workspaceImagesVisible)
  const isOmnichannelAndPricesVisibile = useAppSelector(
    state => state.present.configuration.isOmnichannelAndPricesVisibile,
  )
  const isImagesVisible = useAppSelector(state => state.present.configuration.isImagesVisible)
  const fetchedImages = useAppSelector(state => state.present.images.fetchedImages)
  const islimitQtyVisible = useAppSelector(state => state.present.configuration.limitQtyModule)

  const isQuantitiesFetched = articleSelected.fetchedQuantities
  const isAvailabilitiesFetched = articleSelected.fetchedAvailabilites
  const isMovementsFetched = articleSelected.fetchedMovements
  const isMarketplaceAvailabilitiesFetched = articleSelected.fetchedMarketplaceAvailabilities
  const isPriceDataFetched = articleSelected.fetchedPriceData
  const isQuantityLimitFetched = articleSelected.fetchedQuantityLimitData
  const isDescriptionsFetched = articleSelected.fetchedDescriptions
  const isPubblicationsiteFetched = articleSelected.fetchedPubblicationsite
  const isBarcodesFetched = articleSelected.fetchedBarcodes

  const isQuantityBoxVisible = quantitiesTabVisible && workspaceQuantitiesTabVisible
  const isMovementBoxVisible =
    (movementTabVisible && workspaceMovementVisible) || (availabilityTabVisible && workspaceAvailabilityVisible)
  const isMarketplaceBoxVisible = isMarketplaceAvailabilitiesEnable && workspaceMarketplaceAvailabilitiesVisible
  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  const generateButtonProperties = useCallback(
    (visible: boolean, active: boolean, translation: string, sectionId: string, populateData: any) => {
      const gridIndex = props.gridLayout.findIndex(x => x.i === sectionId)
      if (visible) {
        return {
          id: sectionId,
          active: active,
          className: gridIndex !== -1 && active ? ' active' : '',
          translation: translation,
          order: gridIndex !== -1 ? gridIndex : 10,
          shouldPopulateData: !(gridIndex !== -1 && active),
          populateData,
        }
      } else {
        return null
      }
    },
    [props.gridLayout],
  )

  const gridButtons = useMemo(() => {
    return gridLayoutContainerIdRelation
      .map(element => {
        switch (element.id) {
          case priceGenerationSectionId:
            return isOmnichannelAndPricesVisibile
              ? generateButtonProperties(
                  workspacePricesVisible,
                  isPriceDataFetched,
                  TranslationKeys.buttonPriceSection,
                  element.id,
                  handlePricesData,
                )
              : null
          case quantitySectionId:
            return generateButtonProperties(
              isQuantityBoxVisible,
              isQuantitiesFetched,
              TranslationKeys.buttonSituationSection,
              element.id,
              handleArticleQuantities,
            )
          case marketplaceSectionId:
            return generateButtonProperties(
              isMarketplaceBoxVisible,
              isMarketplaceAvailabilitiesFetched,
              TranslationKeys.buttonAvailabilitySection,
              element.id,
              handleMarketplaceAvailabilitiesData,
            )
          case movementSectionId:
            return generateButtonProperties(
              isMovementBoxVisible,
              isMovementsFetched || isAvailabilitiesFetched,
              TranslationKeys.buttonMovementsSection,
              element.id,
              handleMovementsData,
            )
          case quantityLimitSectionId:
            return isOmnichannelAndPricesVisibile && islimitQtyVisible
              ? generateButtonProperties(
                  workspaceQuantityLimitVisible,
                  isQuantityLimitFetched,
                  TranslationKeys.buttonQuantityLimitSection,
                  element.id,
                  handleQuantityLimitData,
                )
              : null
          case imagesSectionId:
            return isImagesVisible
              ? generateButtonProperties(
                  workspaceImagesVisible,
                  fetchedImages,
                  TranslationKeys.buttonImages,
                  element.id,
                  handleImageRetrieve,
                )
              : null
          case descriptionsSectionId:
            return isImagesVisible
              ? generateButtonProperties(
                  workspaceImagesVisible,
                  isDescriptionsFetched,
                  TranslationKeys.buttonDescSection,
                  element.id,
                  handleDescriptions,
                )
              : null
          case pubblcationsSectionId:
            return isImagesVisible
              ? generateButtonProperties(
                  true,
                  isPubblicationsiteFetched,
                  TranslationKeys.buttonPubbliSection,
                  element.id,
                  handlePubblications,
                )
              : null
          case barcodesSectionId:
            return isImagesVisible
              ? generateButtonProperties(
                  true,
                  isBarcodesFetched,
                  TranslationKeys.tableBarcodeRecapPopoverBarcode,
                  element.id,
                  handleBarcodes,
                )
              : null
          default:
            return null
        }
      })
      .filter(x => x)
      .sort((a, b) => a.order - b.order)
  }, [
    isImagesVisible,
    fetchedImages,
    generateButtonProperties,
    isAvailabilitiesFetched,
    isMarketplaceAvailabilitiesFetched,
    isMarketplaceBoxVisible,
    isMovementBoxVisible,
    isMovementsFetched,
    isDescriptionsFetched,
    isPriceDataFetched,
    isQuantitiesFetched,
    isQuantityBoxVisible,
    isQuantityLimitFetched,
    isOmnichannelAndPricesVisibile,
    workspaceImagesVisible,
    workspacePricesVisible,
    workspaceQuantityLimitVisible,
  ])

  const switchGridColumns = useCallback(() => {
    if (articleKey) {
      dispatch(setStoredGridLayout(changeGridColumns(props.gridLayout, !props.gridOnOneColumn)))
      dispatch(setGridColumn(!props.gridOnOneColumn))
    }
  }, [articleKey, dispatch, props])

  return (
    <Col span={24} className='section-btn-box'>
      <UpperMenu isGridUpperMenu={true} />
      {gridButtons.map(x => {
        return (
          <Button
            key={'grid-btn-' + x.id}
            type='text'
            className={'btn grid-btn' + x.className}
            onClick={() => {
              props.updateGridLayout(x.id, x.order)
              if (x.shouldPopulateData && x.populateData) x.populateData(articleKey, dispatch)()
            }}
            disabled={!x.active}
          >
            {renderTranslation(language, x.translation)}
          </Button>
        )
      })}
      {workspaceDiwaReactGridLayoutColumnsBtnVisibile ? (
        <BorderOuterOutlined
          className={
            'generic-icon icon-grid' + (!articleKey ? ' disable' : '') + (!props.gridOnOneColumn ? '  active ' : '')
          }
          onClick={switchGridColumns}
        />
      ) : null}
    </Col>
  )
}

export default GridLayoutButtons
